import type { ReactElement } from 'react';
import { useNavigate } from 'react-router';
import type { SeriesSettings, SeriesSettingsBody } from '@contracts/series';
import { useTemplates } from '../api/queries';
import { MenuButton, Switch } from '../ui';

type Props = {
  s: SeriesSettings;
  save: (body: SeriesSettingsBody, text?: string) => void;
};

/**
 * The series' default template (PRD 65): every meeting of it is written up
 * with it and opens on it, and it is what the series sends to Slack. The full
 * note is never replaced — one click away on every meeting.
 *
 * Vladimir, 2026-10-08: *"selected template be applied in the series settings
 * and this template is the main note"* — and, of a label that said Slack, *"its
 * not only slack, its the default template that is applied as well"*.
 */
export function DefaultTemplate({ s, save }: Props): ReactElement {
  const templates = useTemplates();
  const navigate = useNavigate();
  const current = s.noteTemplateId
    ? (templates.data?.find((t) => t.id === s.noteTemplateId)?.name ??
      'A template that was deleted')
    : 'Full note';
  return (
    <div className="setting">
      <span className="k">Default template</span>
      <div className="v">
        <MenuButton
          size="sm"
          label={current}
          search
          items={[
            {
              label: 'Full note',
              sub: 'Everything the meeting covered, in Ptitim’s own shape',
              current: !s.noteTemplateId,
              onSelect: () =>
                save({ noteTemplateId: null }, 'Every meeting of it opens on the full note.'),
            },
            ...(templates.data ?? []).map((t) => ({
              id: t.id,
              label: t.name,
              sub: t.description,
              current: t.id === s.noteTemplateId,
              // A series belongs to everybody in it, so its note is written
              // only with a template everybody in it can see.
              disabledBecause:
                t.kind === 'personal'
                  ? 'Only yours — share it with the workspace to use it on a series'
                  : undefined,
              onSelect: () =>
                save(
                  { noteTemplateId: t.id },
                  `Every meeting of it is written up as “${t.name}” and opens on it — the full note is a click away.`,
                ),
            })),
          ]}
          fixed={[{ label: 'Manage templates…', onSelect: () => navigate('/templates') }]}
        />
        <span className="why">
          every meeting is written up with it and opens on it; the full note is a click away
        </span>
      </div>
    </div>
  );
}

/**
 * The note — in the default template — to the series' Slack channel after
 * every meeting, kept up to date there (PRD 65). Usable once a channel is
 * chosen, and saying who will read it; a refusal Slack gave is said here with
 * what to do about it.
 */
export function SendToSlack({ s, save }: Props): ReactElement {
  const channel = s.slackChannelName ? `#${s.slackChannelName}` : null;
  return (
    <>
      <div className="setting">
        <span className="k">Send the note to Slack</span>
        <div className="v">
          <Switch
            label=""
            aria-label="Send the note to Slack after every meeting"
            checked={s.postNote}
            disabled={!s.slackChannelId}
            onChange={(e) =>
              save(
                { postNote: e.target.checked },
                e.target.checked
                  ? `After every meeting the note goes to ${channel}, and follows every change made to it.`
                  : 'The note stays in Ptitim.',
              )
            }
          />
          <span className="why">
            {channel
              ? `after every meeting, kept up to date — everyone in ${channel} will read it`
              : 'choose the Slack channel above first'}
          </span>
        </div>
      </div>
      {s.notePostError ? (
        <div className="setting">
          <span className="k" />
          <div className="v">
            <span className="amber" role="status">
              {s.notePostError}
            </span>
          </div>
        </div>
      ) : null}
    </>
  );
}
